import {
  Menu as MenuIcon,
  School as SchoolIcon,
  Dashboard as DashboardIcon,
  Headphones as HeadphonesIcon,
  MenuBook as MenuBookIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Subscriptions as SubscriptionsIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar as MuiAvatar,
  Chip,
  Badge,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "../store";
import Avatar from "./Avatar";
import { useInterfaceLanguage } from "../hooks/useInterfaceLanguage";
import { useSubscription } from "../hooks/useSubscription";
import {
  selectUser,
  selectIsAnonymous,
} from "../store/selectors/authSelectors";
import { selectCurrentText } from "../store/selectors/practiceSelectors";
import { performLogout } from "../store/utils/logout";

const drawerWidth = 280;
interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  enabled: boolean;
}

interface NavigationContext {
  mode: "minimal" | "full";
  contextualTitle?: string;
  contextualSubtitle?: string;
  hasBottomPanel: boolean;
}

const NavBar: React.FC = () => {
  const { t: translate } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { t: tPractice } = useTranslation("practice");
  const user = useAppSelector(selectUser);
  const isAnonymous = useAppSelector(selectIsAnonymous);
  const currentText = useAppSelector(selectCurrentText);
  const dispatch = useAppDispatch();
  const pendingTeacherInvitations = useAppSelector(
    (state) =>
      state.students.student.relationships.filter(
        (entry): boolean => entry.status === "INVITED",
      ).length,
  );
  const { currentPlan } = useSubscription();
  useInterfaceLanguage();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Language switcher removed — UI follows user's native language automatically

  // Navigation logic
  const getNavigationContext = (): NavigationContext => {
    const pathname = location.pathname;

    // Practice pages - minimal mode with audio panel
    if (pathname.startsWith("/practice/")) {
      return {
        mode: "minimal",
        contextualTitle: currentText?.topic
          ? `${tPractice("title")}: "${currentText.topic}"`
          : tPractice("title"),
        contextualSubtitle: currentText?.level
          ? `${currentText.language?.toUpperCase()} • ${t(`levels.${currentText.level}`)}`
          : undefined,
        hasBottomPanel: true,
      };
    }

    // Dictation session pages - minimal mode with audio panel
    if (
      pathname.startsWith("/dictation/") ||
      pathname === "/dictionary/adaptive"
    ) {
      return {
        mode: "minimal",
        contextualTitle: t(
          pathname === "/dictionary/adaptive"
            ? "nav.dictionary"
            : "nav.dictations",
        ),
        hasBottomPanel: true,
      };
    }

    // Default - full mode for main pages
    return {
      mode: "full",
      hasBottomPanel: false,
    };
  };

  const navigationContext = getNavigationContext();

  const navigationItems: NavigationItem[] = [
    {
      label: t("nav.home"),
      path: "/home",
      icon: <DashboardIcon />,
      enabled: true,
    },
    {
      label: t("nav.texts"),
      path: "/texts",
      icon: <MenuBookIcon />,
      enabled: true,
    },
    {
      label: t("nav.dictations"),
      path: "/dictation",
      icon: <HeadphonesIcon />,
      enabled: true,
    },
    {
      label: t("nav.dictionary"),
      path: "/dictionary",
      icon: <MenuBookIcon />,
      enabled: true,
    },
  ];

  if (!isAnonymous && currentPlan?.planType === "Pro")
    navigationItems.push({
      label: translate("texts:students.title"),
      path: "/students",
      icon: <SchoolIcon />,
      enabled: true,
    });

  const handleDrawerToggle = (): void => setMobileOpen((prev) => !prev);
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>): void =>
    setAnchorEl(e.currentTarget);
  const handleMenuClose = (): void => setAnchorEl(null);

  // No manual language switching handlers

  const handleLogout = (): void => {
    // Keep current UI language so login page stays in user's language
    performLogout(dispatch);
    navigate("/login", { replace: true });
  };

  const getTitle = (): string => {
    if (navigationContext.contextualTitle)
      return navigationContext.contextualTitle;

    // Default titles based on path
    if (location.pathname.startsWith("/texts")) return t("nav.texts");
    if (location.pathname.startsWith("/dictation")) return t("nav.dictations");
    if (location.pathname.startsWith("/dictionary")) return t("nav.dictionary");
    if (location.pathname.startsWith("/profile")) return t("nav.profile");

    return "LingoShade";
  };

  const getSubscriptionStatus = (): string => {
    return user?.subscriptionStatus || "free";
  };

  const getSubscriptionChip = (): React.ReactNode => {
    const status = getSubscriptionStatus();
    switch (status) {
      case "ACTIVE":
        return (
          <Chip
            label={translate("plans:premium.name")}
            size="small"
            color="primary"
            sx={{
              fontSize: "0.75rem",
              height: 20,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        );
      case "PENDING":
        return (
          <Chip
            label={translate("common:interface.pending")}
            size="small"
            sx={{
              fontSize: "0.75rem",
              height: 20,
              bgcolor: "orange",
              color: "primary.main",
              "& .MuiChip-label": { px: 1 },
            }}
          />
        );
      case "INACTIVE":
        return (
          <Chip
            label={translate("common:interface.expired")}
            size="small"
            color="error"
            variant="outlined"
            sx={{
              fontSize: "0.75rem",
              height: 20,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        );
      default:
        return (
          <Chip
            label={translate("plans:free.name")}
            size="small"
            variant="outlined"
            sx={{
              fontSize: "0.75rem",
              height: 20,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        );
    }
  };

  // Mobile drawer content
  const renderMobileDrawer = (): React.ReactNode => (
    <Box
      sx={{
        width: drawerWidth,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* User Profile Section */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <MuiAvatar
            sx={{
              width: 48,
              height: 48,
              mr: 2,
              bgcolor: theme.palette.primary.main,
              fontSize: "1.25rem",
              fontWeight: "bold",
            }}
          >
            {user?.firstName?.[0]?.toUpperCase() ||
              user?.email?.[0]?.toUpperCase() ||
              "U"}
          </MuiAvatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email || t("nav.user")}
            </Typography>
            <Box sx={{ mt: 0.5 }}>{getSubscriptionChip()}</Box>
          </Box>
        </Box>
      </Box>

      {/* Main Navigation */}
      <List sx={{ py: 1 }}>
        {navigationItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              selected={isActive}
              sx={{
                mx: 1,
                mb: 0.5,
                borderRadius: 2,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "& .MuiListItemIcon-root": {
                    color: theme.palette.primary.contrastText,
                  },
                  "& .MuiListItemText-primary": {
                    fontWeight: "bold",
                  },
                },
                "&:hover": {
                  backgroundColor: isActive
                    ? theme.palette.primary.dark
                    : theme.palette.action.hover,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: isActive ? "bold" : "normal",
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ mx: 2 }} />

      {/* Settings Navigation */}
      <List sx={{ py: 1 }}>
        {isAnonymous ? (
          <ListItemButton
            component={Link}
            to="/settings"
            onClick={handleDrawerToggle}
            sx={{ mx: 1, borderRadius: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary={t("nav.settings")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
        ) : (
          <ListItemButton
            component={Link}
            to="/profile"
            onClick={handleDrawerToggle}
            sx={{ mx: 1, borderRadius: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText
              primary={t("nav.profile")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
        )}
        <ListItemButton
          component={Link}
          to="/subscription"
          onClick={handleDrawerToggle}
          sx={{ mx: 1, borderRadius: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <SubscriptionsIcon />
          </ListItemIcon>
          <ListItemText
            primary={t("nav.subscription")}
            primaryTypographyProps={{ variant: "body2" }}
          />
        </ListItemButton>
      </List>

      <Divider sx={{ mx: 2 }} />

      {/* Language switcher removed by design: UI language = user's native language */}

      {/* System Section */}
      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ mx: 2 }} />
      <List sx={{ py: 1 }}>
        <ListItemButton
          onClick={() => {
            handleLogout();
            handleDrawerToggle();
          }}
          sx={{
            mx: 1,
            borderRadius: 2,
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.contrastText,
              "& .MuiListItemIcon-root": {
                color: theme.palette.error.contrastText,
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary={t("nav.logout")}
            primaryTypographyProps={{
              variant: "body2",
              fontWeight: "medium",
            }}
          />
        </ListItemButton>
      </List>
    </Box>
  );

  // Desktop navigation
  const renderDesktopNavigation = (): React.ReactNode => (
    <Box
      component="nav"
      sx={{ display: "flex", alignItems: "center", gap: 0.5, mx: 2 }}
    >
      {navigationItems.map(({ label, path, enabled }) => {
        const isActive =
          location.pathname.startsWith(path) ||
          (path === "/texts" && location.pathname.startsWith("/practice/"));
        return (
          <Button
            key={path}
            component={Link}
            to={enabled ? path : "#"}
            disabled={!enabled}
            aria-current={isActive ? "page" : undefined}
            color="inherit"
            sx={{
              textTransform: "none",
              fontWeight: isActive ? "bold" : "normal",
              minWidth: "auto",
              px: 2,
              py: 1,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: isActive ? "100%" : "0%",
                height: 2,
                backgroundColor: "primary.main",
                transition: theme.transitions.create(["width"], {
                  duration: theme.transitions.duration.short,
                }),
              },
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&::after": {
                  width: "100%",
                  backgroundColor: "primary.main",
                },
              },
            }}
          >
            {label}
          </Button>
        );
      })}
    </Box>
  );

  // AppBar content based on mode
  const renderAppBarContent = (): React.ReactNode => {
    if (navigationContext.mode === "minimal") {
      return (
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
              aria-label={t("nav.openMenu")}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getTitle()}
            </Typography>
            {navigationContext.contextualSubtitle && (
              <Typography
                variant="caption"
                display="block"
                sx={{
                  color: "inherit",
                  opacity: 0.8,
                  lineHeight: 1.2,
                }}
              >
                {navigationContext.contextualSubtitle}
              </Typography>
            )}
          </Box>

          {!isMobile && renderDesktopNavigation()}

          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            aria-label={t("nav.profile")}
          >
            <Avatar
              email={user?.email || ""}
              name={
                user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : undefined
              }
              size={32}
            />
          </IconButton>
        </Toolbar>
      );
    }

    // Full mode
    if (isMobile) {
      return (
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
            aria-label={t("nav.openMenu")}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              fontWeight: "bold",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getTitle()}
          </Typography>

          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            aria-label={t("nav.profile")}
          >
            <Avatar
              email={user?.email || ""}
              name={
                user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : undefined
              }
              size={32}
            />
          </IconButton>
        </Toolbar>
      );
    }

    // Desktop full mode
    return (
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          sx={{
            mr: 4,
            fontWeight: "bold",
            color: "primary.main",
          }}
        >
          LingoShade
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {renderDesktopNavigation()}

        <IconButton
          color="inherit"
          onClick={handleMenuOpen}
          aria-label={t("nav.profile")}
        >
          <Avatar
            email={user?.email || ""}
            name={
              user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : undefined
            }
            size={32}
          />
        </IconButton>
      </Toolbar>
    );
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ zIndex: theme.zIndex.appBar }}
      >
        {renderAppBarContent()}
      </AppBar>

      {isMobile && navigationContext.mode === "full" && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            "body[data-keyboard-open='true'] &": { display: "none" },
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: "1px solid",
            borderColor: "divider",
            pb: "env(safe-area-inset-bottom)",
          }}
        >
          <BottomNavigation
            showLabels
            value={
              navigationItems.find((item): boolean =>
                location.pathname.startsWith(item.path),
              )?.path ?? false
            }
          >
            {navigationItems.map(
              (item): React.ReactElement => (
                <BottomNavigationAction
                  key={item.path}
                  component={Link}
                  to={item.path}
                  value={item.path}
                  label={item.label}
                  icon={item.icon}
                  sx={{
                    minWidth: 0,
                    px: 0.5,
                    "& .MuiBottomNavigationAction-label": { fontSize: ".7rem" },
                  }}
                />
              ),
            )}
          </BottomNavigation>
        </Paper>
      )}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: theme.palette.background.paper,
            },
          }}
        >
          {renderMobileDrawer()}
        </Drawer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {isAnonymous ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate("/settings");
            }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            {t("nav.settings")}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate("/profile");
            }}
          >
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            {t("nav.profile")}
          </MenuItem>
        )}
        {!isAnonymous && (
          <MenuItem
            onClick={(): void => {
              handleMenuClose();
              navigate("/students?role=student");
            }}
          >
            <ListItemIcon>
              <SchoolIcon fontSize="small" />
            </ListItemIcon>
            <Badge
              badgeContent={pendingTeacherInvitations}
              color="primary"
              sx={{ pr: 2 }}
            >
              {translate("texts:students.myTeachers")}
            </Badge>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate("/subscription");
          }}
        >
          <ListItemIcon>
            <SubscriptionsIcon fontSize="small" />
          </ListItemIcon>
          {t("nav.subscription")}
        </MenuItem>
        {/* Language menu removed: no manual switching */}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleLogout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t("nav.logout")}
        </MenuItem>
      </Menu>

      {/* Language selection menu removed */}
    </>
  );
};

export default NavBar;
